/* eslint-disable import/prefer-default-export */
import { SITE_ORIGIN } from 'constants/site';
import { toUrl } from 'util/link';

// array of paths
const _history = typeof window !== 'undefined' ? [] : undefined;

/**
 * wrapper/abstraction around tracking a pageview in Parsely.
 *
 * we're effectively using the Dynamic Tracking approach with an event queue
 * described here: https://www.parse.ly/help/integration/dynamic/
 *
 * we need this abstraction because we've set autotrack to false in the initial
 * parsely config. autotrack is set to false because we utilize client-side
 * routing and need to have direct control over when and how we log page views.
 *
 * if parsely is loaded, we'll call trackPageView() directly. otherwise, we'll
 * add the pageview event to the eventQueue.
 *
 * when calling trackPageView() with manually-prepared event data, we need to
 * pass in the previous page URL. the most straightforward way to do this is by
 * keeping track of visited URLs manually via our private `_history` array,
 * which is append-only.
 *
 * @param {String} path path to page, including query string
 * @param {String} title page title
 */
export function pageView(path, title) {
  if (!_history) {
    return;
  }
  // for landing pages (or with client-side routing disabled), prevPath ends up
  // undefined, so set urlref to document.referrer
  const prevPath = _history[_history.length - 1];
  const urlref =
    prevPath === undefined ? document.referrer : `${SITE_ORIGIN}${prevPath}`;

  const url = `${SITE_ORIGIN}${path}`;
  const parsedUrl = toUrl(url);
  const isPaidTraffic =
    parsedUrl && parsedUrl.searchParams.get('utm_medium') === 'paid';

  // disable heartbeats for paid traffic; re-enable heartbeats on next organic
  // pageview.
  window.PARSELY.enableHeartbeats = !isPaidTraffic;

  // bail out early for paid traffic since we don't want to track pageviews
  if (isPaidTraffic) {
    return;
  }

  // see https://www.parse.ly/help/integration/faq/#what-data-is-sent-by-default
  const eventData = {
    url, // current URL to be tracked
    urlref, // HTTP referrer (traffic source); can be blank
    title, // page title
    js: 1 // flag indicating this is dynamic
  };

  const { eventQueue, loaded } = window.parselyPreload;

  if (loaded) {
    window.PARSELY.beacon.trackPageView(eventData);
  } else {
    eventQueue.push(eventData);
  }

  // Update history
  _history.push(path);
}
